import React, { useState, useEffect } from "react"
import { motion } from "framer-motion"

const SHAPES = {
    gantt: [
        [0, 1],
        [1, 1],
        [2, 1],
        [1, 2],
        [2, 2],
        [3, 2],
        [4, 2],
        [2, 3],
        [3, 3],
    ],
    triangle: [
        [2, 0],
        [1, 1],
        [3, 1],
        [0, 2],
        [2, 2],
        [4, 2],
    ],
    square: [
        [0, 0],
        [1, 0],
        [2, 0],
        [3, 0],
        [4, 0],
        [0, 1],
        [4, 1],
        [0, 2],
        [2, 2],
        [4, 2],
        [0, 3],
        [4, 3],
        [0, 4],
        [1, 4],
        [2, 4],
        [3, 4],
        [4, 4],
    ],
    leave: [
        [1, 0],
        [3, 0],
        [0, 1],
        [1, 1],
        [2, 1],
        [3, 1],
        [4, 1],
        [0, 2],
        [2, 2],
        [4, 2],
        [0, 3],
        [3, 3],
        [4, 3],
        [0, 4],
        [1, 4],
        [2, 4],
        [3, 4],
        [4, 4],
    ],
    time: [
        [2, 0],
        [0, 2],
        [2, 1],
        [4, 2],
        [2, 2],
        [3, 2],
        [2, 4],
    ],
    chart: [
        [0, 4],
        [2, 2],
        [2, 3],
        [2, 4],
        [4, 0],
        [4, 1],
        [4, 2],
        [4, 3],
        [4, 4],
    ],
    sparkle: [
        [2, 0],
        [2, 1],
        [0, 2],
        [1, 2],
        [2, 2],
        [3, 2],
        [4, 2],
        [2, 3],
        [2, 4],
    ],
    resources: [
        [0, 1],
        [0, 2],
        [0, 3],
        [2, 0],
        [2, 1],
        [2, 2],
        [2, 3],
        [2, 4],
        [4, 1],
        [4, 2],
        [4, 3],
    ],

    analytics: [
        [2, 0],
        [0, 2],
        [4, 2],
        [2, 4],
        [2, 2],
        [3, 1],
        [4, 1],
        [2, 2],
        [2, 2],
    ],
}

const SHAPE_KEYS = Object.keys(SHAPES)
const ACCENT_COLORS = {
    cyan: {
        bg: "#00f2fe",
        shadow: "0 0 %1$spx #00f2fe, 0 0 %2$spx rgba(0, 242, 254, 0.6)",
    },
    gold: {
        bg: "#ffb300",
        shadow: "0 0 %1$spx #ffb300, 0 0 %2$spx rgba(255, 215, 0, 0.9)",
    },
    pinkRed: {
        bg: "#fd2a7d",
        shadow: "0 0 %1$spx #fd2a7d, 0 0 %2$spx rgba(253, 42, 125, 0.9)",
    },
}

const HeroDots = ({ size = 200, speed = 1, holdTime = 1, className = "" }) => {
    const [shapeIndex, setShapeIndex] = useState(0)
    const [accentMap, setAccentMap] = useState({})

    const moveDuration = 0.8 / speed
    const actualHoldTime = holdTime / speed
    const totalCycleTime = moveDuration + actualHoldTime

    useEffect(() => {
        const interval = setInterval(() => {
            setShapeIndex((prev) => {
                const nextIndex = (prev + 1) % SHAPE_KEYS.length
                const nextShapeCoords = SHAPES[SHAPE_KEYS[nextIndex]]

                // Pick 1 or 2 random dot indexes to turn Gold or Pink/Red (#fd2a7d)
                const count = Math.random() > 0.5 ? 2 : 1
                const newAccents = {}

                for (let i = 0; i < count; i++) {
                    const randomIndex = Math.floor(Math.random() * nextShapeCoords.length)
                    const color = Math.random() > 0.5 ? "gold" : "pinkRed"
                    newAccents[randomIndex] = color
                }

                setAccentMap(newAccents)
                return nextIndex
            })
        }, totalCycleTime * 1000)

        return () => clearInterval(interval)
    }, [totalCycleTime])

    const activeKey = SHAPE_KEYS[shapeIndex]
    const currentCoords = SHAPES[activeKey]

    const dotSize = Math.max(8, Math.round(size * 0.08))
    const innerGridSize = Math.round(size * 0.75)

    return (
        <div
            className={className}
            style={{
                width: size,
                height: "auto",
                display: "inline-flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                boxSizing: "border-box",
            }}
        >
            <div
                style={{
                    position: "relative",
                    width: innerGridSize,
                    height: innerGridSize,
                }}
            >
                {currentCoords.map(([col, row], dotIndex) => {
                    const xPx = (col / 4) * innerGridSize
                    const yPx = (row / 4) * innerGridSize

                    // Determine if this specific dot gets a gold or pinkRed accent
                    const colorKey = accentMap[dotIndex] || "cyan"
                    const colorConfig = ACCENT_COLORS[colorKey]

                    const shadowStyle = colorConfig.shadow
                        .replace("%1$s", (dotSize * 0.9).toString())
                        .replace("%2$s", (dotSize * 1.8).toString())

                    return (
                        <motion.div
                            key={dotIndex}
                            animate={{
                                x: xPx,
                                y: yPx,
                                backgroundColor: colorConfig.bg,
                                boxShadow: shadowStyle,
                            }}
                            transition={{
                                type: "tween",
                                ease: "easeInOut",
                                duration: moveDuration,
                            }}
                            style={{
                                position: "absolute",
                                left: 0,
                                top: 0,
                                width: `${dotSize}px`,
                                height: `${dotSize}px`,
                                borderRadius: "50%",
                                margin: `-${dotSize / 2}px 0 0 -${dotSize / 2}px`,
                                willChange: "transform, background-color, box-shadow",
                            }}
                        />
                    )
                })}
            </div>
        </div>
    )
}

export default HeroDots
